/**
 * Deterministic cover gradient from a stable form identifier.
 * Matches the original PublicFormShell indigo cover — monotone app, one accent band.
 * Per-form variety: cool hue family only (slate → indigo → violet), same stop structure.
 */

function hashFormId(formId) {
  const input = String(formId ?? 'default');
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function hslColor(h, s, l) {
  return `hsl(${(h + 360) % 360} ${s}% ${l}%)`;
}

/** Cool professional hues — not full rainbow; fits zinc UI. */
const COVER_BASE_HUES = [210, 222, 234, 246, 258, 270];

function coverBaseHue(formId) {
  return COVER_BASE_HUES[hashFormId(formId) % COVER_BASE_HUES.length];
}

/** Original light cover stop shape (#e0e7ff → #4f46e5), shifted by base hue. */
function buildLightCoverGradient(baseHue) {
  const stops = [
    [baseHue - 6, 92, 93, 0],
    [baseHue - 6, 89, 89, 20],
    [baseHue - 1, 93, 82, 40],
    [baseHue + 2, 90, 74, 65],
    [baseHue + 9, 86, 67, 85],
    [baseHue + 10, 76, 58, 100],
  ];
  const parts = stops.map(([h, s, l, pct]) => `${hslColor(h, s, l)} ${pct}%`);
  return `linear-gradient(135deg, ${parts.join(', ')})`;
}

/** Original dark cover stop shape, color stops follow base hue. */
function buildDarkCoverGradient(baseHue) {
  return [
    'hsl(0 0% 6%) 0%',
    `${hslColor(baseHue + 6, 28, 14)} 25%`,
    `${hslColor(baseHue - 8, 48, 16)} 50%`,
    `${hslColor(baseHue - 16, 72, 22)} 75%`,
    `${hslColor(baseHue + 20, 42, 35)} 100%`,
  ].join(', ');
}

export function getFormCoverGradient(formId, { isDark = false } = {}) {
  const baseHue = coverBaseHue(formId ?? 'default');
  return isDark
    ? `linear-gradient(135deg, ${buildDarkCoverGradient(baseHue)})`
    : buildLightCoverGradient(baseHue);
}

// ponytail: self-check — stable per id, distinct across ids; throws in dev if broken
if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
  const a = getFormCoverGradient('form-a');
  const b = getFormCoverGradient('form-b');
  if (a === b || getFormCoverGradient('form-a') !== a) {
    throw new Error('formCoverGradient self-check failed');
  }
  if (a.includes('radial-gradient')) {
    throw new Error('formCoverGradient self-check failed: expected linear cover');
  }
}
